import { createFileRoute, Link } from "@tanstack/react-router";
import { useSuspenseQuery } from "@tanstack/react-query";
import { imageUrl } from "@opinly/shared";
import { z } from "zod";
import { blogPostsQuery } from "@/lib/opinly-queries";
import { formatPostDate, opinlyConfig, SITE_URL } from "@/lib/opinly";
import {
  BLOG_LANGS,
  BLOG_LANG_LABELS,
  filterPostsByLanguage,
  type BlogLang,
} from "@/lib/opinly-lang";

const CANONICAL = `${SITE_URL}/blog`;
const TITLE = "Blog, Costa Blanca travel tips from Casa Mi Pajobe";
const DESCRIPTION =
  "Stories, travel tips and practical guides about La Nucia, Benidorm and the Costa Blanca, written by the hosts of Casa Mi Pajobe.";

export const Route = createFileRoute("/blog/")({
  validateSearch: (search: Record<string, unknown>): { cursor?: string; lang?: BlogLang } => {
    const cursor = z.string().max(512).optional().catch(undefined).parse(search["cursor"]);
    const lang = z.enum(BLOG_LANGS).optional().catch(undefined).parse(search["lang"]);
    return { ...(cursor ? { cursor } : {}), ...(lang ? { lang } : {}) };
  },
  loaderDeps: ({ search }) => ({ cursor: search.cursor }),
  loader: ({ context, deps }) => context.queryClient.ensureQueryData(blogPostsQuery(deps.cursor)),
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "robots", content: "index,follow,max-image-preview:large,max-snippet:-1" },
      { name: "description", content: DESCRIPTION },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESCRIPTION },
      { property: "og:url", content: CANONICAL },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [{ rel: "canonical", href: CANONICAL }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Blog",
          name: TITLE,
          description: DESCRIPTION,
          url: CANONICAL,
          isPartOf: { "@id": `${SITE_URL}/#website` },
        }),
      },
    ],
  }),
  component: BlogIndex,
  pendingComponent: BlogIndexPending,
  errorComponent: BlogError,
  notFoundComponent: BlogNotFound,
});

function BlogIndexPending() {
  return (
    <main className="mx-auto max-w-5xl px-4 py-12">
      <div className="h-9 w-56 animate-pulse rounded bg-muted" />
      <ul className="mt-10 grid gap-8 sm:grid-cols-2">
        {Array.from({ length: 4 }).map((_, i) => (
          <li key={i} className="space-y-3">
            <div className="aspect-[16/9] animate-pulse rounded-xl bg-muted" />
            <div className="h-5 w-3/4 animate-pulse rounded bg-muted" />
            <div className="h-4 w-full animate-pulse rounded bg-muted" />
          </li>
        ))}
      </ul>
    </main>
  );
}

function BlogNotFound() {
  return <BlogError />;
}

function BlogError({ error }: { error?: Error }) {
  if (error) console.error(error);
  return (
    <main className="mx-auto max-w-2xl px-4 py-24 text-center">
      <h1 className="text-2xl font-semibold text-foreground">The blog didn't load</h1>
      <p className="mt-2 text-sm text-muted-foreground">
        Please try again in a moment, or head back to the villa.
      </p>
      <Link
        to="/"
        className="mt-6 inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90"
      >
        Go home
      </Link>
    </main>
  );
}

function BlogIndex() {
  const { cursor, lang } = Route.useSearch();
  const { data } = useSuspenseQuery(blogPostsQuery(cursor));
  const allPosts = data.data ?? [];
  const posts = filterPostsByLanguage(allPosts, lang);

  return (
    <main className="mx-auto max-w-5xl px-4 py-12">
      <header>
        <h1 className="font-serif text-3xl font-semibold tracking-tight text-foreground sm:text-4xl">
          Blog
        </h1>
        <p className="mt-3 max-w-2xl text-base text-muted-foreground">{DESCRIPTION}</p>
      </header>

      <nav aria-label="Taal" className="mt-6 flex flex-wrap gap-2">
        <Link
          to="/blog"
          search={cursor ? { cursor } : {}}
          className={`rounded-full border px-3 py-1.5 text-sm font-medium transition ${
            lang
              ? "border-input bg-background text-foreground hover:bg-accent"
              : "border-primary bg-primary text-primary-foreground"
          }`}
        >
          Alle talen
        </Link>
        {BLOG_LANGS.map((code) => (
          <Link
            key={code}
            to="/blog"
            search={{ ...(cursor ? { cursor } : {}), lang: code }}
            className={`rounded-full border px-3 py-1.5 text-sm font-medium transition ${
              lang === code
                ? "border-primary bg-primary text-primary-foreground"
                : "border-input bg-background text-foreground hover:bg-accent"
            }`}
          >
            {BLOG_LANG_LABELS[code]}
          </Link>
        ))}
      </nav>

      {posts.length === 0 ? (
        <p className="mt-16 text-center text-muted-foreground">
          {lang
            ? `Nog geen artikels in het ${BLOG_LANG_LABELS[lang]}, kijk later nog eens terug.`
            : "No articles published yet, please check back soon."}
        </p>
      ) : (
        <ul className="mt-10 grid gap-10 sm:grid-cols-2">
          {posts.map((post) => {
            const src = post.image?.fileKey ? imageUrl(post.image.fileKey, opinlyConfig) : null;
            return (
              <li key={post.slug}>
                <article className="group flex h-full flex-col">
                  <Link
                    to="/blog/$slug"
                    params={{ slug: post.slug }}
                    className="flex h-full flex-col rounded-xl focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                  >
                    {src ? (
                      <img
                        src={src}
                        alt={post.image?.alt ?? post.title}
                        loading="lazy"
                        className="aspect-[16/9] w-full rounded-xl border border-border/60 object-cover transition group-hover:opacity-90"
                      />
                    ) : null}
                    <div className="mt-4 flex flex-1 flex-col">
                      <p className="text-xs uppercase tracking-wide text-muted-foreground">
                        {post.category?.name ? `${post.category.name} · ` : ""}
                        <time dateTime={post.firstPublishedAt}>
                          {formatPostDate(post.firstPublishedAt)}
                        </time>
                      </p>
                      <h2 className="mt-2 text-lg font-semibold text-foreground group-hover:underline">
                        {post.title}
                      </h2>
                      <p className="mt-2 line-clamp-3 text-sm text-muted-foreground">
                        {post.description}
                      </p>
                    </div>
                  </Link>
                </article>
              </li>
            );
          })}
        </ul>
      )}

      <nav aria-label="Pagination" className="mt-14 flex justify-between gap-3">
        {cursor ? (
          <Link
            to="/blog"
            search={lang ? { lang } : {}}
            className="inline-flex items-center rounded-md border border-input bg-background px-4 py-2 text-sm font-medium text-foreground hover:bg-accent"
          >
            ← Latest articles
          </Link>
        ) : (
          <span />
        )}
        {data.has_more && data.next_cursor ? (
          <Link
            to="/blog"
            search={{ cursor: data.next_cursor, ...(lang ? { lang } : {}) }}
            className="inline-flex items-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90"
          >
            Older articles →
          </Link>
        ) : null}
      </nav>
    </main>
  );
}
